import './settings.css';
import { Button, Popconfirm, Table, message } from 'antd';
import { DatabaseOutlined, DeleteOutlined, ReloadOutlined, UndoOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { serverFetch } from '@server';
import { showError } from '@extensions/utils';

const Backup = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [restarting, setRestarting] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        serverFetch('backup')
            .then(setItems)
            .catch(showError)
            .finally(() => setLoading(false));
    }, []);

    useEffect(load, [load]);

    const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

    const restart = () => {
        setRestarting(true);
        serverFetch('service/status')
            .then((before) => serverFetch('service/restart', { method: 'POST' }).then(() => before))
            .then(async (before) => {
                message.success('API перезапускается');
                const started = Date.now();
                let sawDown = false;
                while (Date.now() - started < 40000) {
                    await sleep(1000);
                    try {
                        const status = await serverFetch('service/status');
                        if (sawDown || status.pid !== before.pid) {
                            message.success('API снова доступен');
                            setRestarting(false);
                            return;
                        }
                    } catch {
                        sawDown = true;
                    }
                }
                message.warning('API не ответил за 40 секунд');
                setRestarting(false);
            })
            .catch((e) => {
                showError(e);
                setRestarting(false);
            });
    };

    const create = () => {
        setCreating(true);
        serverFetch('backup', { method: 'POST' })
            .then(() => {
                message.success('Резервная копия создана');
                load();
            })
            .catch(showError)
            .finally(() => setCreating(false));
    };

    const restore = (name) => {
        serverFetch(`backup/${name}/restore`, { method: 'POST' })
            .then(() => {
                message.success('База восстановлена');
                window.location.reload();
            })
            .catch(showError);
    };

    const remove = (name) => {
        serverFetch(`backup/${name}`, { method: 'DELETE' })
            .then(() => {
                message.success('Копия удалена');
                load();
            })
            .catch(showError);
    };

    return (
        <div className="backup-page">
            <div className="crud-table-header"><h3>Приложение</h3></div>
            <Popconfirm
                title="Перезапустить API? Сервер ненадолго перестанет отвечать."
                okText="Перезапустить"
                cancelText="Отмена"
                onConfirm={restart}
            >
                <Button icon={<ReloadOutlined />} loading={restarting}>Перезапустить API</Button>
            </Popconfirm>
            <div style={{ color: '#999', margin: '8px 0 24px' }}>
                Перезапуск занимает несколько секунд. На сервере служба поднимается через systemd.
            </div>
            <div className="crud-table-header"><h3>Резервные копии</h3></div>
            <Button type="primary" icon={<DatabaseOutlined />} loading={creating} onClick={create}>
                Создать резервную копию
            </Button>
            <div style={{ color: '#999', margin: '8px 0 16px' }}>
                Копия включает базу данных (все фирмы) и файлы вложений.
                Папка хранения задаётся в appsettings Backup:Path.
            </div>
            <Table
                size="small"
                rowKey="name"
                loading={loading}
                pagination={false}
                dataSource={items}
                columns={[
                    {
                        title: 'Дата и время',
                        dataIndex: 'createdOn',
                        render: (v) => dayjs(v).format('DD.MM.YYYY HH:mm:ss'),
                    },
                    { title: 'Имя', dataIndex: 'name' },
                    {
                        title: 'Размер',
                        dataIndex: 'size',
                        width: 100,
                        align: 'right',
                        render: (v) => (v / 1048576).toFixed(1) + ' МБ',
                    },
                    {
                        title: 'Файлы',
                        dataIndex: 'hasFiles',
                        width: 70,
                        render: (v) => (v ? 'Да' : '—'),
                    },
                    {
                        title: '',
                        key: 'actions',
                        width: 110,
                        render: (_, r) => (
                            <>
                                <Popconfirm
                                    title="Восстановить базу и файлы из этой копии? Текущие данные всех фирм будут заменены."
                                    okText="Да"
                                    cancelText="Нет"
                                    onConfirm={() => restore(r.name)}
                                >
                                    <Button size="small" icon={<UndoOutlined />} title="Восстановить" />
                                </Popconfirm>
                                <Popconfirm
                                    title="Удалить резервную копию?"
                                    okText="Да"
                                    cancelText="Нет"
                                    onConfirm={() => remove(r.name)}
                                >
                                    <Button size="small" danger icon={<DeleteOutlined />} style={{ marginLeft: 4 }} title="Удалить" />
                                </Popconfirm>
                            </>
                        ),
                    },
                ]}
            />
        </div>
    );
};

export default Backup;
